/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { useCallback } from 'react';

import SelectField from './SelectField';

import styles from '../../styles/fields/table-field.module.css';

interface TableFieldValue {
    [key: string]: string | string[] | null;
}

interface TableFieldProps {
    name: string;
    rows: FieldOption[];
    columns: FieldOption[];
    value?: TableFieldValue | null;
    multiple?: boolean;
    className?: string | null;
    onChange?: (value: TableFieldValue | null) => void;
}

function RangeField({
    name,
    value = null,
    rows,
    columns,
    multiple = false,
    className = null,
    onChange = null,
}: TableFieldProps) {
    const isSelect = !multiple;
    const onInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, rowName: string) => {
            const { value: columnValue = null, tagName } = event.currentTarget;
            const { checked = columnValue !== null && columnValue.length > 0 } =
                event.currentTarget;
            const newValue = { ...value };
            if (newValue[rowName] === undefined) {
                newValue[rowName] = multiple ? [] : null;
            }
            if (multiple) {
                if (checked) {
                    newValue[rowName] = [...(newValue[rowName] as string[]), columnValue];
                } else {
                    newValue[rowName] = (newValue[rowName] as string[]).filter(
                        (v) => v !== columnValue,
                    );
                }
            } else {
                newValue[rowName] = checked ? columnValue : null;
            }
            if (onChange !== null) {
                onChange(newValue);
            }
        },
        [value, multiple, onChange],
    );

    const onSelectChange = useCallback(
        (rowName: string, selectValue: string | null) => {
            const newValue = {
                ...value,
                [rowName]: selectValue !== null && selectValue !== '' ? selectValue : null,
            };
            if (onChange !== null) {
                onChange(newValue);
            }
        },
        [value, onChange],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isSelect]: isSelect,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                <table className={styles.table}>
                    {!isSelect ? (
                        <thead>
                            <tr className={styles.header}>
                                <th />
                                {columns.map(({ value: columnValue, label: columnLabel }) => (
                                    <th
                                        key={`column-${columnValue}`}
                                        align="center"
                                        className={styles.headerCell}
                                    >
                                        {columnLabel}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    ) : null}
                    <tbody>
                        {rows.map(({ value: rowName, label: rowLabel }) => {
                            const rowValue = value !== null ? value[rowName] : null;
                            return (
                                <tr key={`row-${rowName}`} className={styles.row}>
                                    <td
                                        className={styles.labelCell}
                                        dangerouslySetInnerHTML={{ __html: rowLabel }}
                                    />
                                    {isSelect ? (
                                        <td>
                                            <SelectField
                                                name={`${name}[${rowName}]`}
                                                value={rowValue}
                                                onChange={(newValue) =>
                                                    onSelectChange(rowName, newValue)
                                                }
                                                options={[{ value: '', label: '--' }, ...columns]}
                                                className={styles.select}
                                            />
                                        </td>
                                    ) : (
                                        columns.map(({ value: columnValue }) => {
                                            const checked = multiple
                                                ? (rowValue || []).indexOf(columnValue) !== -1
                                                : rowValue === columnValue;
                                            return (
                                                <td
                                                    key={`cell-${rowName}-${columnValue}`}
                                                    align="center"
                                                    className={styles.inputCell}
                                                >
                                                    <input
                                                        type={multiple ? 'checkbox' : 'radio'}
                                                        name={`${name}[${rowName}]`}
                                                        checked={checked}
                                                        value={columnValue}
                                                        onChange={(e) => onInputChange(e, rowName)}
                                                    />
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default RangeField;
